import React from 'react';
import { Check, CornerUpRight, AlertTriangle, Clock, Users, ChevronRight, Target, FilePlus2, FilePen, Share2, BookOpen, Flame, Minus, Repeat, ListChecks } from 'lucide-react';
import { fmtTimeRange, missedLabel, recurrenceLabel } from './plannerUtils';
import { SharedGoalTick, RosterAvatars, SharedBadge, QuantityGoalControl, ParticipantAvatars } from './GoalShareBits';

const NOTE_META = {
  created: { icon: FilePlus2, verb: 'created' },
  updated: { icon: FilePen, verb: 'updated' },
  shared: { icon: Share2, verb: 'shared with you' },
};

/* Small status pill used for goals (missed / expired / on-track). */
export const GoalStatusBadge = ({ goal }) => {
  const label = missedLabel(goal);
  if (goal.completed) {
    return (
      <span data-testid="planner-goal-status" className="p-tnum inline-flex items-center gap-1 rounded-full border border-[var(--p-border)] bg-[var(--p-card-2)] px-2 py-0.5 text-[10.5px] font-semibold text-[var(--p-success)]">
        <Check size={11} /> Done
      </span>
    );
  }
  if (goal.expired) {
    return (
      <span data-testid="planner-goal-expired-badge" className="inline-flex items-center gap-1 rounded-full border border-[var(--p-border)] bg-[var(--p-card-2)] px-2 py-0.5 text-[10.5px] font-semibold text-[var(--p-danger)]">
        <AlertTriangle size={11} /> Expired
      </span>
    );
  }
  if (label) {
    return (
      <span data-testid="planner-goal-missed-badge" className="inline-flex items-center gap-1 rounded-full border border-[var(--p-border)] bg-[var(--p-accent-bg)] px-2 py-0.5 text-[10.5px] font-semibold text-[var(--p-danger)]">
        <CornerUpRight size={11} /> {label}
      </span>
    );
  }
  return null;
};

/* Compact progress pill for a COUNTED weekly goal (e.g. 3/4 days). */
export const CountedBadge = ({ goal }) => {
  if (goal.period_met) {
    return (
      <span data-testid="planner-goal-counted-met" className="inline-flex items-center gap-1 rounded-full border border-[color:var(--p-accent-soft)] bg-[color:var(--p-accent-bg)] px-2 py-0.5 text-[10.5px] font-semibold text-[color:var(--p-accent)]">
        <Check size={11} strokeWidth={3} /> Met
      </span>
    );
  }
  if (goal.missed_count > 0) {
    return (
      <span data-testid="planner-goal-counted-missed" className="inline-flex items-center gap-1 rounded-full border border-[color:var(--p-border)] bg-[color:var(--p-accent-bg)] px-2 py-0.5 text-[10.5px] font-semibold text-[color:var(--p-danger)]">
        <CornerUpRight size={11} /> Missed {goal.missed_count} wk{goal.missed_count > 1 ? 's' : ''}
      </span>
    );
  }
  const left = Math.max(0, (goal.target_count || 0) - (goal.period_count || 0));
  return (
    <span data-testid="planner-goal-counted-remaining" className="inline-flex items-center gap-1 rounded-full border border-[color:var(--p-border)] bg-[color:var(--p-card-2)] px-2 py-0.5 text-[10.5px] font-semibold text-[color:var(--p-text-soft)]">
      {left} to go
    </span>
  );
};

/* Compact met / remaining pill for a QUANTITY goal. */
export const QuantityBadge = ({ goal }) => {
  if (goal.period_met) {
    return (
      <span data-testid="planner-goal-qty-met" className="inline-flex items-center gap-1 rounded-full border border-[color:var(--p-accent-soft)] bg-[color:var(--p-accent-bg)] px-2 py-0.5 text-[10.5px] font-semibold text-[color:var(--p-accent)]">
        <Check size={11} strokeWidth={3} /> Met
      </span>
    );
  }
  const left = Math.max(0, (goal.target || goal.quantity_target || 0) - (goal.period_count || 0));
  return (
    <span data-testid="planner-goal-qty-remaining" className="inline-flex items-center gap-1 rounded-full border border-[color:var(--p-border)] bg-[color:var(--p-card-2)] px-2 py-0.5 text-[10.5px] font-semibold text-[color:var(--p-text-soft)]">
      {left} to go
    </span>
  );
};

/* A single goal line with a tick box for the current period.
   For COUNTED weekly goals the tick reflects TODAY's day-tick and a
   "x/target this week" progress line is shown.
   For QUANTITY goals a +/- counter replaces the checkbox. */
export const GoalRow = ({ goal, onToggle, onQuantity, onEdit, compact = false }) => {
  const counted = !!goal.is_counted;
  const quantity = !!goal.is_quantity;
  const shared = !!goal.is_shared;

  if (quantity) {
    const unitWord = goal.unit || 'period';
    const contributors = goal.contributors || goal.roster || [];
    return (
      <div
        data-testid="planner-goal-item"
        className={`group flex items-start gap-2.5 rounded-[var(--p-radius-md)] px-2.5 ${compact ? 'py-1.5' : 'py-2'} transition-colors hover:bg-[color:var(--p-accent-bg)]/40`}
      >
        <div className="mt-0.5 shrink-0">
          <QuantityGoalControl goal={goal} onQuantity={onQuantity} />
        </div>
        <div className="min-w-0 flex-1">
          <button
            type="button"
            onClick={() => onEdit && onEdit(goal)}
            className="n-focus flex w-full items-center gap-1.5 text-left text-[13.5px] text-[color:var(--p-text)]"
          >
            <span className="truncate">{goal.title}</span>
            {shared && <SharedBadge count={(goal.shared_with || []).length + 1} />}
          </button>
          <div className="mt-0.5 flex items-center gap-1 text-[11.5px] text-[color:var(--p-muted)]">
            <span className="p-tnum font-semibold text-[color:var(--p-text-soft)]">{goal.period_count || 0}/{goal.target || goal.quantity_target}</span>
            <span>{goal.unit_label || 'done'} this {unitWord}</span>
            {shared && goal.count_mode === 'team' && typeof goal.my_count === 'number' && (
              <span className="text-[color:var(--p-muted-2)]">&middot; you {goal.my_count}</span>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {shared && <RosterAvatars roster={contributors} logs={goal.logs || []} title="Logged by" />}
          <QuantityBadge goal={goal} />
        </div>
      </div>
    );
  }

  const checked = !!goal.completed; // day-tick (counted) or period-tick (classic); team-based when shared
  const strike = !counted && checked;
  return (
    <div
      data-testid="planner-goal-item"
      className={`group flex items-start gap-2.5 rounded-[var(--p-radius-md)] px-2.5 ${compact ? 'py-1.5' : 'py-2'} transition-colors hover:bg-[color:var(--p-accent-bg)]/40`}
    >
      {shared ? (
        <SharedGoalTick goal={goal} onToggle={onToggle} />
      ) : (
        <button
          type="button"
          data-testid="planner-goal-toggle-checkbox"
          onClick={() => onToggle && onToggle(goal)}
          aria-label={checked ? 'Untick for today' : 'Tick for today'}
          aria-pressed={checked}
          className={`n-focus mt-0.5 grid h-[18px] w-[18px] shrink-0 place-items-center rounded-[6px] border transition-colors ${
            checked
              ? 'border-[var(--p-accent)] bg-[var(--p-accent)] text-white'
              : 'border-[var(--p-muted-2)] bg-[var(--p-card)] hover:border-[var(--p-accent)]'
          }`}
        >
          {checked && <Check size={13} strokeWidth={3} />}
        </button>
      )}
      <div className="min-w-0 flex-1">
        <button
          type="button"
          onClick={() => onEdit && onEdit(goal)}
          className="n-focus flex w-full items-center gap-1.5 text-left text-[13.5px] text-[color:var(--p-text)]"
        >
          <span className={`truncate ${strike ? 'text-[color:var(--p-muted)] line-through' : ''}`}>{goal.title}</span>
          {shared && <SharedBadge count={(goal.shared_with || []).length + 1} />}
        </button>
        {counted ? (
          <div className="mt-0.5 flex items-center gap-1 text-[11.5px] text-[color:var(--p-muted)]">
            <span className="p-tnum font-semibold text-[color:var(--p-text-soft)]">{goal.period_count || 0}/{goal.target_count}</span>
            <span>days this week</span>
          </div>
        ) : (goal.description && !compact) ? (
          <div className="mt-0.5 truncate text-[11.5px] text-[color:var(--p-muted)]">{goal.description}</div>
        ) : null}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {shared && <RosterAvatars roster={goal.roster} title="Ticked by" />}
        {counted ? <CountedBadge goal={goal} /> : <GoalStatusBadge goal={goal} />}
      </div>
    </div>
  );
};

/* Circular progress ring (conic-gradient). Children render centered. */
export const ProgressRing = ({ pct = 0, size = 68, thickness = 7, children }) => (
  <div
    data-testid="planner-progress-ring"
    className="relative grid shrink-0 place-items-center rounded-full"
    style={{ width: size, height: size, background: `conic-gradient(var(--p-accent) ${pct}%, var(--p-line) ${pct}% 100%)` }}
  >
    <div className="grid place-items-center rounded-full bg-[color:var(--p-card)] text-center" style={{ width: size - thickness * 2, height: size - thickness * 2 }}>
      {children}
    </div>
  </div>
);

/* Streak flame + count (hidden at 0). */
export const StreakBadge = ({ count }) => {
  if (!count) return null;
  return (
    <span data-testid="planner-streak-badge" className="inline-flex items-center gap-0.5 rounded-full border border-[color:var(--p-accent-soft)] bg-[color:var(--p-accent-bg)] px-1.5 py-0.5 text-[10.5px] font-bold text-[color:var(--p-accent)]" title={`${count}-${count === 1 ? 'period' : 'period'} streak`}>
      <Flame size={11} /> {count}
    </span>
  );
};

/* One habit-grid cell. Tickable when done / missed / today; disabled for
   out-of-range and upcoming days. */
export const HabitTick = ({ cell, onToggle, size = 26 }) => {
  const { state, date } = cell;
  const tickable = state === 'done' || state === 'miss' || state === 'today';
  return (
    <button
      type="button"
      disabled={!tickable}
      onClick={() => tickable && onToggle && onToggle(date)}
      data-testid={`planner-habit-tick-${date}`}
      aria-label={`${state === 'done' ? 'Done' : 'Not done'} on ${date}`}
      aria-pressed={state === 'done'}
      className={`n-focus p-tick p-tick-${state} ${tickable ? 'cursor-pointer' : 'cursor-default'}`}
      style={{ width: size, height: size }}
    >
      {state === 'done' && <Check size={14} strokeWidth={3.2} />}
      {state === 'miss' && <i />}
      {state === 'soon' && <i />}
      {state === 'oor' && <Minus size={12} strokeWidth={2.5} />}
    </button>
  );
};

/* Priority dot colour map (reuses token palette; neutral when none). */
const PRIORITY_CLS = {
  high: 'bg-[var(--p-danger)]',
  medium: 'bg-[var(--p-accent)]',
  low: 'bg-[var(--p-muted-2)]',
};

/* A read-only task row that opens in the Tasks module. */
export const TaskRow = ({ task, onOpen, compact = false }) => {
  return (
    <button
      type="button"
      data-testid="planner-task-row"
      onClick={() => onOpen && onOpen(task.id)}
      aria-label="Open task in Tasks module"
      className={`n-focus group flex w-full items-start justify-between gap-3 rounded-[var(--p-radius-md)] px-2.5 ${compact ? 'py-1.5' : 'py-2'} text-left transition-colors hover:bg-[color:var(--p-accent-bg)]/40`}
    >
      <span className="flex min-w-0 items-start gap-2">
        <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${task.completed ? 'bg-[var(--p-success)]' : (PRIORITY_CLS[task.priority] || 'bg-[var(--p-muted-2)]')}`} />
        <span className="min-w-0">
          <span data-testid={task.completed ? 'planner-task-completed' : undefined}
            className={`block truncate text-[13.5px] ${task.completed ? 'text-[color:var(--p-muted)] line-through' : 'text-[color:var(--p-text)]'}`}>{task.title}</span>
          {task.carried_over && !task.completed && (
            <span data-testid="planner-task-carried-over-badge"
              className="mt-1 inline-flex items-center gap-1 rounded-full border border-[var(--p-border)] bg-[var(--p-accent-bg)] px-1.5 py-0.5 text-[10px] font-semibold text-[color:var(--p-text-soft)]">
              <CornerUpRight size={10} /> Carried over · due {task.due_date}
            </span>
          )}
        </span>
      </span>
      <ChevronRight size={15} className="mt-1 shrink-0 text-[color:var(--p-muted-2)] opacity-0 transition-opacity group-hover:opacity-100" />
    </button>
  );
};

/* Linked-tasks completion summary (e.g. "2 of 3 completed"). Rendered on
   event/meeting surfaces across Daily & Weekly. Returns null when the item has
   no linked tasks. ``compact`` renders a tight "2/3" variant for cramped bars. */
export const LinkedTasksBadge = ({ tasks, total, done, compact = false }) => {
  const t = typeof total === 'number' ? total : (tasks ? tasks.length : 0);
  if (!t) return null;
  const d = typeof done === 'number'
    ? done
    : (tasks ? tasks.filter((x) => x.completed).length : 0);
  const allDone = d >= t;
  return (
    <span
      data-testid="planner-linked-tasks-badge"
      title={`${d} of ${t} linked task${t > 1 ? 's' : ''} completed`}
      className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-semibold ${
        allDone
          ? 'border-[color:var(--p-success)]/40 bg-[color:var(--p-success)]/12 text-[color:var(--p-success)]'
          : 'border-[color:var(--p-border)] bg-[color:var(--p-card-2)] text-[color:var(--p-text-soft)]'
      }`}
    >
      <ListChecks size={11} className="shrink-0" />
      {compact ? `${d}/${t}` : `${d} of ${t} completed`}
    </span>
  );
};

/* Compact event / meeting chip (weekly + untimed strips). */
export const ItemChip = ({ item, onOpen }) => {
  const isMeeting = item.kind === 'meeting';
  const tasks = item.tasks || [];
  return (
    <button
      type="button"
      data-testid="planner-item-chip"
      onClick={() => onOpen && onOpen(item)}
      className={`n-focus block w-full rounded-[10px] border px-2.5 py-1.5 text-left text-[12px] transition-shadow hover:shadow-md ${
        isMeeting
          ? 'border-[var(--p-border)] bg-[var(--p-accent-bg)]'
          : 'border-[var(--p-border)] bg-[var(--p-card-2)]'
      }`}
    >
      <span className="flex items-center gap-1.5">
        {isMeeting ? <Users size={11} className="shrink-0 text-[color:var(--p-accent)]" /> : <Clock size={11} className="shrink-0 text-[color:var(--p-muted)]" />}
        {item.start_time && <span className="p-tnum shrink-0 text-[10.5px] text-[color:var(--p-muted)]">{item.start_time}</span>}
        <span className="truncate text-[color:var(--p-text)]">{item.title}</span>
        <span className="ml-auto flex shrink-0 items-center gap-1">
          <ParticipantAvatars item={item} size={14} max={3} />
          {item.recurring && <Repeat size={10} className="shrink-0 text-[color:var(--p-muted-2)]" aria-label={recurrenceLabel(item.recurrence, item.anchor_date)} />}
        </span>
      </span>
      {tasks.length > 0 && (
        <span className="mt-1 flex">
          <LinkedTasksBadge tasks={tasks} />
        </span>
      )}
    </button>
  );
};

export const SectionTitle = ({ icon: Icon, children, action }) => (
  <div className="mb-2 flex items-center justify-between">
    <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-[color:var(--p-muted-2)]">
      {Icon && <Icon size={13} />} {children}
    </div>
    {action}
  </div>
);

/* A note-activity line (created / updated / shared with you). Opens the note. */
export const NoteRow = ({ note, onOpen, compact = false }) => {
  const meta = NOTE_META[note.activity] || NOTE_META.updated;
  const Icon = meta.icon;
  return (
    <button
      type="button"
      data-testid="planner-note-row"
      onClick={() => onOpen && onOpen(note.note_id)}
      aria-label={`Open note ${note.title}`}
      className={`n-focus group flex w-full items-start gap-2 rounded-[var(--p-radius-md)] px-2.5 ${compact ? 'py-1.5' : 'py-2'} text-left transition-colors hover:bg-[color:var(--p-accent-bg)]/40`}
    >
      <Icon size={14} className="mt-0.5 shrink-0 text-[color:var(--p-accent)]" />
      <span className="min-w-0 flex-1 truncate text-[13px] text-[color:var(--p-text)]">
        <span className="font-medium">{note.title}</span>
        <span className="text-[color:var(--p-muted)]"> {meta.verb}</span>
      </span>
      <ChevronRight size={15} className="mt-0.5 shrink-0 text-[color:var(--p-muted-2)] opacity-0 transition-opacity group-hover:opacity-100" />
    </button>
  );
};

/* Compact note chip for weekly columns. */
export const NoteChip = ({ note, onOpen }) => {
  const meta = NOTE_META[note.activity] || NOTE_META.updated;
  const Icon = meta.icon;
  return (
    <button
      type="button"
      data-testid="planner-note-chip"
      onClick={() => onOpen && onOpen(note.note_id)}
      className="n-focus block w-full truncate rounded-[10px] border border-dashed border-[var(--p-border)] bg-transparent px-2.5 py-1.5 text-left text-[12px] transition-colors hover:bg-[color:var(--p-accent-bg)]/40"
    >
      <span className="flex items-center gap-1.5">
        <Icon size={11} className="shrink-0 text-[color:var(--p-accent)]" />
        <span className="truncate text-[color:var(--p-text-soft)]">{note.title} <span className="text-[color:var(--p-muted-2)]">{meta.verb}</span></span>
      </span>
    </button>
  );
};

export { Target, BookOpen };
